import { Editor } from 'codemirror';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controlled as CodeMirrorComponent } from 'react-codemirror2';

import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/python/python';

import { useI18nContext } from 'components/I18nProvider';
import { InlineTab, InlineTabs } from 'components/InlineTabs';
import PythonCodeTestPanel, { SelectWinnerTestResult, TestInputs } from './PythonCodeTestPanel';
import './PythonCodeEditor.scss';

const PYTHON_KEYWORDS = [
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
];

const PYTHON_BUILTINS = [
  'abs',
  'all',
  'any',
  'bool',
  'dict',
  'enumerate',
  'filter',
  'float',
  'format',
  'frozenset',
  'getattr',
  'hasattr',
  'hash',
  'int',
  'isinstance',
  'issubclass',
  'iter',
  'len',
  'list',
  'map',
  'max',
  'min',
  'next',
  'object',
  'open',
  'ord',
  'print',
  'property',
  'range',
  'repr',
  'reversed',
  'round',
  'set',
  'setattr',
  'slice',
  'sorted',
  'str',
  'sum',
  'super',
  'tuple',
  'type',
  'vars',
  'zip',
];

function pythonHint(editor: Editor, contextWords: string[]) {
  const cur = editor.getCursor();
  const curLine = editor.getLine(cur.line);
  let end = cur.ch;
  let start = end;
  while (start > 0 && /[\w$]/.test(curLine.charAt(start - 1))) start--;
  const curWord = curLine.slice(start, end);

  if (!curWord) return undefined;

  const allWords = [...PYTHON_KEYWORDS, ...PYTHON_BUILTINS, ...contextWords];

  // Also collect words already in the editor
  const editorText = editor.getValue();
  const wordPattern = /[\w$]+/g;
  let match;
  const seen = new Set(allWords);
  while ((match = wordPattern.exec(editorText)) !== null) {
    if (match[0] !== curWord && !seen.has(match[0]) && match[0].length > 1) {
      seen.add(match[0]);
      allWords.push(match[0]);
    }
  }

  const filtered = allWords.filter((w) => w.startsWith(curWord) && w !== curWord);

  if (filtered.length === 0) return undefined;

  return {
    list: filtered,
    from: { line: cur.line, ch: start },
    to: { line: cur.line, ch: end },
  };
}

export interface PythonCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  defaultComment: string;
  contextWords?: string[];
  isSelectWinner?: boolean;
  onSelectWinnerTestResult?: (result: SelectWinnerTestResult) => void;
  selectWinnerTestResult?: SelectWinnerTestResult;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  savedTestInputs?: TestInputs;
  onTestInputsChange?: (inputs: TestInputs) => void;
}

const PythonCodeEditor = ({
  value,
  onChange,
  defaultComment,
  contextWords = [],
  isSelectWinner = true,
  onSelectWinnerTestResult,
  selectWinnerTestResult,
  activeTab: controlledTab,
  onActiveTabChange,
  savedTestInputs,
  onTestInputsChange,
}: PythonCodeEditorProps) => {
  const { tc } = useI18nContext();
  const [internalTab, setInternalTab] = useState('code');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = onActiveTabChange ?? setInternalTab;
  const testPanelRef = useRef<HTMLDivElement>(null);
  const codePanelRef = useRef<HTMLDivElement>(null);
  const hintFn = useCallback((editor: Editor) => pythonHint(editor, contextWords), [contextWords]);

  const options = useMemo(
    () => ({
      mode: 'python',
      lineNumbers: true,
      lineWrapping: true,
      matchBrackets: true,
      autoCloseBrackets: true,
      extraKeys: {
        'Ctrl-Space': 'autocomplete',
      },
      hintOptions: {
        hint: hintFn,
        completeSingle: false,
      },
    }),
    [hintFn]
  );

  useEffect(() => {
    if (!value) {
      onChange(defaultComment);
    }
    // Only initialize once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh CodeMirror instances when switching tabs, since editors
  // initialized under display:none don't know their dimensions.
  useEffect(() => {
    const container = activeTab === 'test' ? testPanelRef.current : codePanelRef.current;
    if (container) {
      container.querySelectorAll('.CodeMirror').forEach((el: any) => {
        el.CodeMirror?.refresh();
      });
    }
  }, [activeTab]);

  const handleInputRead = useCallback(
    (editor: Editor) => {
      const cur = editor.getCursor();
      const token = editor.getTokenAt(cur);
      // Trigger autocomplete when typing an identifier of 2+ chars (not in comments/strings)
      if (
        token.type !== 'comment' &&
        token.type !== 'string' &&
        token.string.length >= 2 &&
        /[\w$]/.test(token.string)
      ) {
        editor.showHint({ hint: hintFn, completeSingle: false });
      }
    },
    [hintFn]
  );

  return (
    <div className="python-code-editor">
      <InlineTabs selectedTab={activeTab} onChange={setActiveTab}>
        <InlineTab id="code">{tc('code')}</InlineTab>
        <InlineTab id="test">{tc('test')}</InlineTab>
      </InlineTabs>
      <div ref={codePanelRef} style={{ display: activeTab === 'code' ? 'block' : 'none' }}>
        <CodeMirrorComponent
          value={value}
          options={options}
          onBeforeChange={(_editor, _data, newValue) => {
            onChange(newValue);
          }}
          editorDidMount={(editor) => {
            editor.on('inputRead', handleInputRead);
          }}
        />
      </div>
      <div ref={testPanelRef} style={{ display: activeTab === 'test' ? 'block' : 'none' }}>
        <PythonCodeTestPanel
          code={value}
          isSelectWinner={isSelectWinner}
          onSelectWinnerTestResult={onSelectWinnerTestResult}
          selectWinnerTestResult={selectWinnerTestResult}
          savedTestInputs={savedTestInputs}
          onTestInputsChange={onTestInputsChange}
        />
      </div>
    </div>
  );
};

export default PythonCodeEditor;
